import { alpha, Components, Theme } from "@mui/material";
import { outlinedSecondarySurfaceStyles, secondarySurfaceStyles } from "./secondary-surface";

export const vrButtonOverride: Components<Theme>["MuiButton"] = {
  defaultProps: {
    disableElevation: true,
    variant: "contained",
    color: "primary",
    size: "large",
  },
  styleOverrides: {
    root: ({ ownerState, theme }) => ({
      textTransform: "none",
      boxShadow: "none",
      transition: "background-color 0.25s ease, color 0.25s ease, opacity 0.25s ease",
      cornerShape: "squircle",
      "& .MuiTypography-root, & .MuiCircularProgress-root": {
        color: "inherit",
      },
      "& .MuiTypography-root, & > span, & div": {
        transition: "color 0.25s ease, opacity 0.25s ease",
      },
      "&:hover": {
        boxShadow: "none",
      },
      ...(ownerState.variant === "contained" &&
        ownerState.color === "tertiary" && {
          backgroundColor: theme.palette.tertiary.main,
          color: theme.palette.tertiary.contrastText,
          "&:hover": {
            backgroundColor: theme.palette.tertiary.dark,
            color: theme.palette.tertiary.contrastText,
            boxShadow: "none",
          },
        }),
      "&.Mui-disabled": {
        backgroundColor: theme.palette.dsSurface.v400,
        color: theme.palette.dsNeutral.v300,
      },
      "&[data-shape='pill']": {
        borderRadius: "100px",
        cornerShape: "unset",
      },
      "&[data-status='not_started']": {
        color: theme.palette.dsNeutral.v400,
        "&.Mui-disabled": {
          backgroundColor: theme.palette.dsSurface.v100,
        },
      },
      "&[data-status='completed']": {
        color: theme.palette.dsSuccess.v400,
        "&.Mui-disabled": {
          backgroundColor: theme.palette.dsSuccess.v200,
        },
      },
      "&[data-status='failed']": {
        color: theme.palette.dsDestructive.v400,
        "&.Mui-disabled": {
          backgroundColor: theme.palette.dsDestructive.v100,
        },
      },
      "&[data-status='winner']": {
        color: theme.palette.dsWarning.v300,
        "&.Mui-disabled": {
          backgroundColor: theme.palette.dsWarning.v100,
        },
      },
    }),
    sizeSmall: ({ theme }) => ({
      ...theme.typography.textBase500,
      padding: theme.spacing(1.5, 2.5),
      borderRadius: theme.spacing(2),
      "& .MuiCircularProgress-root": {
        width: theme.spacing(2.5),
        height: theme.spacing(2.5),
      },
    }),
    sizeMedium: ({ theme }) => ({
      ...theme.typography.text1xl600,
      padding: theme.spacing(2, 3.5),
      borderRadius: theme.spacing(2.5),
      "& .MuiCircularProgress-root": {
        width: theme.spacing(2.5),
        height: theme.spacing(2.5),
      },
    }),
    sizeLarge: ({ theme }) => ({
      ...theme.typography.text1xl600,
      padding: theme.spacing(2, 3.5),
      borderRadius: theme.spacing(2.5),
      "& .MuiCircularProgress-root": {
        width: theme.spacing(3),
        height: theme.spacing(3),
      },
    }),
    contained: {
      border: "none",
    },
    containedPrimary: ({ theme }) => ({
      backgroundColor: theme.palette.dsPrimary.v300,
      color: theme.palette.common.white,
      "&:hover": {
        backgroundColor: alpha(theme.palette.dsPrimary.v300, 0.8),
        color: theme.palette.common.white,
      },
    }),
    containedSecondary: ({ theme }) => secondarySurfaceStyles(theme),
    containedInfo: ({ theme }) => ({
      backgroundColor: theme.palette.common.white,
      color: theme.palette.common.black,
      "&:hover": {
        backgroundColor: alpha(theme.palette.common.white, 0.8),
        color: theme.palette.common.black,
      },
    }),
    outlined: ({ theme }) => ({
      backgroundColor: "transparent",
      color: theme.palette.dsBase.v200,
      borderColor: theme.palette.dsSurface.v400,
      "&:hover": {
        backgroundColor: theme.palette.dsSurface.v100,
        borderColor: theme.palette.dsSurface.v400,
      },
    }),
    outlinedSecondary: ({ theme }) => ({
      ...outlinedSecondarySurfaceStyles(theme),
    }),
    text: ({ theme }) => ({
      backgroundColor: theme.palette.dsSurface.v100,
      color: theme.palette.dsBase.v200,
      opacity: 0.4,
      "&:hover": {
        backgroundColor: theme.palette.common.white,
        color: theme.palette.common.black,
        opacity: 1,
      },
    }),
  },
};

export const portalButtonOverride: Components<Theme>["MuiButton"] = {
  defaultProps: {
    disableElevation: true,
    variant: "contained",
    color: "primary",
  },
  styleOverrides: {
    root: ({ theme }) => ({
      ...theme.typography.textS500,
      minWidth: "auto",
      minHeight: theme.spacing(5.5),
      padding: theme.spacing(1, 1.5),
      borderRadius: theme.spacing(0.75),
      textTransform: "none",
      transition: "all 0.2s ease",
      boxShadow: "none",
      "&.Mui-disabled": {
        opacity: 0.6,
      },
    }),
    contained: ({ theme }) => ({
      backgroundColor: theme.palette.dsPrimary.v300,
      color: theme.palette.common.white,
      border: "none",
      "&:hover": {
        backgroundColor: theme.palette.dsPrimary.v400,
        boxShadow: "none",
      },
      "&:active": {
        backgroundColor: theme.palette.dsPrimary.v400,
      },
      "&.Mui-focusVisible": {
        boxShadow: `0 0 0 ${theme.spacing(0.2)} ${theme.palette.dsPrimary.v400}`,
        outline: "none",
      },
    }),
    outlined: ({ theme }) => ({
      backgroundColor: "transparent",
      color: theme.palette.text.primary,
      border: `${theme.spacing(0.125)} solid ${theme.palette.dsSurface.v400}`,
      "&:hover": {
        backgroundColor: theme.palette.action.hover,
        border: `${theme.spacing(0.125)} solid ${theme.palette.dsSurface.v400}`,
      },
      "&:active": {
        backgroundColor: theme.palette.action.selected,
      },
      "&.Mui-focusVisible": {
        boxShadow: `0 0 0 ${theme.spacing(0.2)} ${theme.palette.dsPrimary.v200}`,
        outline: "none",
      },
    }),
    startIcon: ({ theme }) => ({
      marginLeft: 0,
      marginRight: theme.spacing(1.5),
    }),
    endIcon: ({ theme }) => ({
      marginLeft: theme.spacing(1.5),
      marginRight: 0,
    }),
  },
};
